import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ShoppingBag, AlertCircle } from 'lucide-react';
import api from '../api/client';
import { useCart } from '../context/CartContext';

/**
 * Post-Stripe-Checkout landing page. Polls /payments/stripe/status/{session_id}
 * until the transaction is marked paid, then shows the freshly-created order.
 */
const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState('loading'); // 'loading' | 'paid' | 'failed' | 'timeout'
  const [tx, setTx] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const cart = useCart();
  const refreshedCart = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }
    let mounted = true;
    let attempt = 0;
    const maxAttempts = 12; // ~24s total at 2s intervals
    const poll = async () => {
      if (!mounted) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const r = await api.get(`/payments/stripe/status/${sessionId}`);
        setTx(r.data);
        if (r.data.payment_status === 'paid') {
          setState('paid');
          if (!refreshedCart.current) {
            refreshedCart.current = true;
            cart?.refresh?.();
          }
          return;
        }
        if (['failed', 'expired'].includes(r.data.payment_status)) {
          setState('failed');
          return;
        }
      } catch (e) {
        // continue polling — transient
      }
      if (attempt < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setState('timeout');
      }
    };
    poll();
    return () => { mounted = false; };
  }, [sessionId]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 min-h-[70vh] flex items-center">
      <div className="w-full bg-white border border-neutral-200 rounded-sm p-8 md:p-10 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-[#F46A25] mx-auto mb-4 animate-spin" data-testid="payment-loading" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Confirming your payment…</h1>
            <p className="text-sm text-neutral-600">
              Stripe is processing your card. This usually takes a couple of seconds.
              {attempts > 1 && <span className="block text-[11px] text-neutral-400 mt-1">Attempt {attempts} / 12</span>}
            </p>
          </>
        )}

        {state === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" data-testid="payment-success" />
            <h1 className="text-3xl font-black text-[#12263A] mb-2">Payment received!</h1>
            <p className="text-sm text-neutral-600 mb-6">
              Thanks — your order has been placed and a receipt is on its way.
            </p>
            <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-4 text-left inline-block mb-6">
              <div className="text-[11px] uppercase font-bold text-neutral-500">Order</div>
              <div className="font-mono text-sm text-[#12263A]">#{(tx?.order_id || '').slice(0, 8) || '—'}</div>
              <div className="text-[11px] uppercase font-bold text-neutral-500 mt-2">Total charged</div>
              <div className="font-black text-lg text-[#F46A25]">
                £{Number(tx?.amount || 0).toFixed(2)}
                <span className="text-[10px] text-neutral-500 uppercase ml-1">(ex VAT — VAT added by Stripe)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/orders" data-testid="view-orders-btn" className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-1">
                <ShoppingBag className="w-4 h-4" /> View your orders
              </Link>
              <Link to="/" className="bg-white border border-neutral-300 hover:border-[#F46A25] text-[#12263A] font-bold text-sm px-4 py-2 rounded-sm">
                Continue shopping
              </Link>
            </div>
          </>
        )}

        {state === 'failed' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" data-testid="payment-failed" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Payment did not complete</h1>
            <p className="text-sm text-neutral-600 mb-6">
              We couldn't confirm your card charge. Your basket has been kept so you can try again.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/cart" className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm">
                Return to basket
              </Link>
              <Link to="/" className="bg-white border border-neutral-300 hover:border-[#F46A25] text-[#12263A] font-bold text-sm px-4 py-2 rounded-sm">
                Home
              </Link>
            </div>
          </>
        )}

        {state === 'timeout' && (
          <>
            <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" data-testid="payment-timeout" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Still processing…</h1>
            <p className="text-sm text-neutral-600 mb-6">
              Your bank is taking a little longer than usual. Once the payment clears we'll
              email your receipt. You can safely close this page — nothing else is needed.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/orders" className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm">
                View orders
              </Link>
              <button onClick={() => window.location.reload()} className="bg-white border border-neutral-300 hover:border-[#F46A25] text-[#12263A] font-bold text-sm px-4 py-2 rounded-sm">
                Check again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
